import type { GridFilterModel } from '@mui/x-data-grid'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useToast } from '~/components/communication/Toast'
import { USERS_TAB_PERMISSIONS } from '~/components/routes/users/tabs'
import { useDeleteUser } from '~/components/routes/users/use-delete-selection'
import { appUsersColumns, SOLVENCY_COLUMN_FIELDS } from '~/components/routes/users/users-columns'
import { UsersHeaderActions } from '~/components/routes/users/users-header-actions'
import {
  PAGE_SIZE_OPTIONS,
  UsersPageLayout,
  UsersSearchGrid,
} from '~/components/routes/users/users-page-layout'
import { useDebounce } from '~/hooks/use-debounce'
import { api } from '~/lib/tuyau'
import { useMigratedRight, withRoutePermissions } from '~/utils/rbac'

export const Route = createFileRoute('/_authenticated/users/app')({
  staticData: {
    permission: USERS_TAB_PERMISSIONS.app,
  },
  component: AppUsersPage,
})
withRoutePermissions(Route)

const SEARCH_DEBOUNCE_MS = 500

const EMPTY_FILTER_MODEL: GridFilterModel = { items: [] }

/** Le filtre booléen de la DataGrid porte la valeur brute du select : `''`, `'true'` ou `'false'`. */
const booleanFilterValue = (model: GridFilterModel, field: string) => {
  const item = model.items.find((candidate) => candidate.field === field)
  if (item?.value === undefined || item.value === '') return undefined
  return item.value === true || item.value === 'true'
}

function AppUsersPage() {
  const [Toast, showToast] = useToast()
  const [search, setSearch] = useState('')
  const [filterModel, setFilterModel] = useState<GridFilterModel>(EMPTY_FILTER_MODEL)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0])
  const [selected, setSelected] = useState<number[]>([])

  const canReadSolvency = useMigratedRight(['read', 'users.app-users.solvency'], [])

  const columns = useMemo(
    () =>
      canReadSolvency
        ? appUsersColumns
        : appUsersColumns.filter((column) => !SOLVENCY_COLUMN_FIELDS.includes(column.field)),
    [canReadSolvency],
  )

  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS)

  const { data, refetch, isFetching } = useQuery(
    api.users.indexApp.queryOptions(
      {
        query: {
          page: page + 1,
          pageSize,
          search: debouncedSearch || undefined,
          isBanned: booleanFilterValue(filterModel, 'isBanned'),
          isReliable: canReadSolvency ? booleanFilterValue(filterModel, 'isReliable') : undefined,
        },
      },
      { placeholderData: keepPreviousData },
    ),
  )

  const handleDelete = useDeleteUser(selected, showToast, () => {
    setSelected([])
    return refetch()
  })

  return (
    <UsersPageLayout
      activeTab="app"
      actions={
        <UsersHeaderActions activeTab="app" selected={selected} handleDelete={handleDelete} />
      }
    >
      <Toast />
      <UsersSearchGrid
        search={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(0)
        }}
        columns={columns}
        rows={data?.items ?? []}
        loading={isFetching}
        onRowSelectionModelChange={setSelected}
        serverPagination={{
          rowCount: data?.total ?? 0,
          page,
          pageSize,
          onChange: (model) => {
            setPage(model.page)
            setPageSize(model.pageSize)
          },
        }}
        serverFilters={{
          model: filterModel,
          // Un filtre modifié peut réduire la liste sous la page courante : on revient au début.
          onChange: (model) => {
            setFilterModel(model)
            setPage(0)
          },
        }}
      />
    </UsersPageLayout>
  )
}
